import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import {
  Panel, Loader, EmptyState, useToast, errText, useCaptchaConfirm, Modal, Pagination, TenantSelect,
} from '../ui';

// Client, organisation and branch must all be selected before anything is
// fetched. Organisations / branches come from the same source as the
// Permissions page: /CPanel/GetOrganization and /CPanel/GetBranch.
// ORG_ID / BRANCH_ID below are only placeholders in the empty templates.
const ORG_ID = 0;
const BRANCH_ID = 0;
const ADMIN_USER = 'admin'; // TODO: wire to the actual logged-in admin user

// formId is database-generated on create — never sent in the CreateForm
// body. See Module_Form_Permission_Sync_API_Documentation.docx §2.1/§2.2.
const emptyForm = {
  organisationId: ORG_ID,
  branchId: BRANCH_ID,
  ModuleId: 0,
  formName: '',
  formUrl: '',
  formSequence: 0,
  description: '',
  fineYear: '',
  isActive: true,
  videoLink: '',
  pdfPath: '',
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const toList = (res) =>
  Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.result) ? res.result
    : [];

const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const truthy = (v) => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

// Modules are only used for the dropdowns / name lookup here.
const normModule = (m) => ({
  moduleId: pick(m, ['moduleId', 'ModuleId', 'moduleID', 'ModuleID', 'id', 'Id']),
  moduleName: pick(m, ['moduleName', 'ModuleName', 'name', 'Name']) ?? '',
});

// Accepts FormId / formId etc. `ModuleId` keeps its capital M because that is
// the name the Create/Update body uses (see emptyForm).
const normForm = (f) => {
  const active = pick(f, ['isActive', 'IsActive']);
  return {
    formId: pick(f, ['formId', 'FormId']),
    ModuleId: pick(f, ['ModuleId', 'moduleId']),
    formName: pick(f, ['formName', 'FormName']) ?? '',
    formUrl: pick(f, ['formUrl', 'FormUrl']) ?? '',
    formSequence: pick(f, ['formSequence', 'FormSequence']) ?? 0,
    description: pick(f, ['description', 'Description']) ?? '',
    fineYear: pick(f, ['fineYear', 'FineYear']) ?? '',
    isActive: active === undefined ? true : truthy(active),
    videoLink: pick(f, ['videoLink', 'VideoLink']) ?? '',
    pdfPath: pick(f, ['pdfPath', 'PdfPath']) ?? '',
  };
};

// ---------------------------------------------------------------------------
// SearchSelect — dropdown with a search box. The list is position:fixed so a
// Panel / Modal with overflow:hidden can't clip it.
//   options = [{ value, label }]      onChange receives the option's value
// ---------------------------------------------------------------------------
function SearchSelect({ options, value, onChange, placeholder = 'select…', disabled, width = 200 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.max(r.width, 240);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    setPos({ top: r.bottom + 4, left, width: w });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, place]);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  // keep the highlighted option visible when navigating with the arrow keys
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('.ss__opt.hi')
      ?.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const needle = q.trim().toLowerCase();
  const filtered = needle ? options.filter((o) => String(o.label).toLowerCase().includes(needle)) : options;
  const selected = options.find((o) => String(o.value) === String(value));

  const toggle = () => {
    setOpen((o) => !o);
    setQ('');
    // start the highlight on the currently selected option
    setHi(Math.max(0, options.findIndex((o) => String(o.value) === String(value))));
  };
  const choose = (o) => { onChange(o.value); setOpen(false); };

  const onSearchKey = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // don't let the Modal close as well
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[hi]) choose(filtered[hi]);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="term-input ss__btn"
        style={{ width }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="ss__label">{selected ? selected.label : placeholder}</span>
        <span className="ss__caret">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="ss__panel"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          onClick={(e) => e.preventDefault()} // stops a wrapping <label> from re-clicking the button
        >
          <div className="ss__search">
            <input
              className="term-input"
              style={{ width: '100%', boxSizing: 'border-box' }}
              autoFocus
              placeholder="search…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setHi(0); }}
              onKeyDown={onSearchKey}
            />
          </div>
          <div className="ss__list" role="listbox" ref={listRef}>
            {filtered.length === 0 && <div className="ss__empty">no match</div>}
            {filtered.map((o, i) => (
              <button
                key={`${i}-${String(o.value)}`}
                type="button"
                role="option"
                aria-selected={String(o.value) === String(value)}
                className={`ss__opt${i === hi ? ' hi' : ''}${String(o.value) === String(value) ? ' sel' : ''}`}
                onMouseEnter={() => setHi(i)}
                onClick={() => choose(o)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

const CSS = `
.ss__btn{display:inline-flex;align-items:center;justify-content:space-between;gap:8px;text-align:left;cursor:pointer}
.ss__btn:disabled{opacity:.5;cursor:not-allowed}
.ss__label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ss__caret{opacity:.7}
.ss__panel{position:fixed;z-index:10000;background:var(--bg-input,#0d1117);border:1px solid var(--green,#3dff7f);border-radius:4px;box-shadow:0 12px 32px rgba(0,0,0,.6)}
.ss__search{padding:8px}
.ss__list{max-height:240px;overflow:auto}
.ss__opt{display:block;width:100%;padding:7px 12px;font:inherit;font-size:12.5px;text-align:left;color:var(--text,#c9d1d9);background:transparent;border:0;cursor:pointer}
.ss__opt.hi{background:rgba(61,255,127,.10)}
.ss__opt.sel{color:var(--green,#3dff7f);font-weight:600}
.ss__empty{padding:12px;font-size:12px;opacity:.7}
`;

export default function FormManagement() {
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  // ---- client -> organisation -> branch ----
  const [clientCode, setClientCode] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [orgId, setOrgId] = useState('');       // strings, for <select>
  const [branchId, setBranchId] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);

  // ---- modules (for the filter + module names) and forms ----
  const [modules, setModules] = useState(null);
  const [moduleFilter, setModuleFilter] = useState('');
  const [forms, setForms] = useState(null);
  const [formsError, setFormsError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deletingName, setDeletingName] = useState(null);
  const [form, setForm] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // data is fetched only after client + organisation + branch are all selected
  const ready = !!clientCode && orgId !== '' && branchId !== '';
  const effOrgId = Number(orgId);
  const effBranchId = Number(branchId);

  // 1. client chosen -> organisations (first one auto-selected)
  useEffect(() => {
    setOrgs([]); setOrgId('');
    setBranches([]); setBranchId('');
    setModuleFilter('');
    if (!clientCode) return undefined;

    let cancelled = false;
    setOrgLoading(true);
    (async () => {
      try {
        const res = await tenantApi.getCpanelOrganizations(clientCode);
        if (cancelled) return;
        const list = toList(res);
        setOrgs(list);
        if (list.length) setOrgId(String(list[0].schoolId));
      } catch (err) {
        if (!cancelled) toast.error(errText(err));
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientCode]); // eslint-disable-line

  // 2. organisation chosen -> branches (first one auto-selected)
  useEffect(() => {
    setBranches([]); setBranchId('');
    setModuleFilter('');
    if (!clientCode || orgId === '') return undefined;

    let cancelled = false;
    setBranchLoading(true);
    (async () => {
      try {
        const res = await tenantApi.getCpanelBranches(orgId, clientCode);
        if (cancelled) return;
        // GetBranch returns a base64 logo per branch — not needed here.
        const list = toList(res).map(({ bytLogo, ...b }) => b); // eslint-disable-line no-unused-vars
        setBranches(list);
        if (list.length) setBranchId(String(list[0].branchId));
      } catch (err) {
        if (!cancelled) toast.error(errText(err));
      } finally {
        if (!cancelled) setBranchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientCode, orgId]); // eslint-disable-line

  // 3a. organisation + branch known -> modules
  const modSeq = useRef(0);
  useEffect(() => {
    const seq = ++modSeq.current;
    setModules(null);
    if (!ready) return;
    (async () => {
      try {
        const res = await tenantApi.getModules(effOrgId, effBranchId);
        if (seq !== modSeq.current) return;
        setModules(toList(res).map(normModule));
      } catch (err) {
        if (seq !== modSeq.current) return;
        setModules([]);
        toast.error(errText(err));
      }
    })();
  }, [ready, effOrgId, effBranchId]); // eslint-disable-line

  // 3b. organisation + branch (+ module filter) known -> forms
  const formSeq = useRef(0);
  const loadForms = useCallback(async () => {
    const seq = ++formSeq.current;
    setForms(null);
    setFormsError(null);
    if (!ready) return;
    try {
      const res = await tenantApi.getForms({
        organisationId: effOrgId,
        branchId: effBranchId,
        moduleId: moduleFilter || undefined,
      });
      if (seq !== formSeq.current) return;
      setForms(toList(res).map(normForm));
      setPage(1);
    } catch (err) {
      if (seq !== formSeq.current) return;
      setFormsError(errText(err));
      toast.error(errText(err));
    }
  }, [ready, effOrgId, effBranchId, moduleFilter]); // eslint-disable-line

  useEffect(() => { loadForms(); }, [loadForms]);

  const openCreate = () => setForm({
    ...emptyForm,
    organisationId: effOrgId,
    branchId: effBranchId,
    ModuleId: moduleFilter ? Number(moduleFilter) : (modules?.[0]?.moduleId ?? 0),
  });
  const openEdit = (f) => setForm({
    ...emptyForm,
    ...f,
    organisationId: effOrgId,
    branchId: effBranchId,
  });
  // The modal can only be dismissed this way — and this is never called
  // while `busy` is true, since Modal itself is non-closable in that state.
  const closeForm = () => setForm(null);

  const submitForm = async () => {
    setBusy(true);
    try {
      if (form.formId) {
        // Update sends updatedBy, not createdBy — formId travels in the URL
        // only and isn't repeated in the body.
        const { formId, ...rest } = form;
        await tenantApi.updateForm(formId, { ...rest, updatedBy: ADMIN_USER });
        toast.success('Form updated.');
      } else {
        const { formId, ...rest } = form;
        await tenantApi.createForm({ ...rest, createdBy: ADMIN_USER });
        toast.success('Form created.');
      }
      closeForm();
      loadForms();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (f) => {
    const ok = await confirm(`Delete form "${f.formName}"?`);
    if (!ok) return;
    setBusy(true);
    setDeletingName(f.formName);
    try {
      // DeleteForm needs no body/query — the audit user comes from the
      // Bearer token server-side.
      await tenantApi.deleteForm(f.formId);
      toast.success('Form deleted.');
      await loadForms(); // wait for the refetch so the loader stays visible
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
      setDeletingName(null);
    }
  };

  const moduleName = (id) =>
    modules?.find((m) => String(m.moduleId) === String(id))?.moduleName ?? id;

  // Filter on the client too, in case the API ignores the moduleId param.
  const filteredForms = (forms || []).filter(
    (f) => !moduleFilter || String(f.ModuleId) === String(moduleFilter),
  );
  const total = filteredForms.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedForms = filteredForms.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pickerLoading = orgLoading || branchLoading;

  return (
    <>
      {confirmNode}
      <style>{CSS}</style>
      <h1 className="page-title">form_management</h1>
      <p className="page-sub">
        GET/POST/PUT/DELETE /api/ModuleForm/*Form — forms nested under a module, per client / organisation / branch ·{' '}
        <Link to="/admin/tenants/tools/modules">← manage modules</Link>
      </p>

      <Panel title="// FORMS">
        <div className="toolbar" style={{ marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
          <div className="toolbar__group">
            <TenantSelect value={clientCode} onSelect={(cc) => setClientCode(cc)} />
          </div>

          <select
            className="term-input"
            value={orgId}
            disabled={busy || !clientCode || !orgs.length}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {!clientCode && <option value="">organisation (pick a client)</option>}
            {clientCode && !orgs.length && <option value="">No organisations</option>}
            {orgs.map((o) => (
              <option key={o.schoolId} value={String(o.schoolId)}>{o.schoolName}</option>
            ))}
          </select>

          <select
            className="term-input"
            value={branchId}
            disabled={busy || !clientCode || !branches.length}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {!clientCode && <option value="">branch (pick a client)</option>}
            {clientCode && !branches.length && <option value="">No branches</option>}
            {branches.map((b) => (
              <option key={b.branchId} value={String(b.branchId)}>{b.branchName}</option>
            ))}
          </select>

          <SearchSelect
            width={220}
            value={moduleFilter}
            disabled={!ready}
            placeholder="all modules"
            options={[
              { value: '', label: 'all modules' },
              ...(modules || []).map((m) => ({ value: String(m.moduleId), label: m.moduleName })),
            ]}
            onChange={(v) => setModuleFilter(String(v))}
          />

          <button
            className="term-btn term-btn--solid term-btn--sm"
            onClick={openCreate}
            disabled={!ready || busy || !modules?.length}
          >
            + new_form
          </button>
        </div>

        {!ready && pickerLoading && <Loader label="loading organisations and branches" />}
        {!ready && !pickerLoading && (
          <EmptyState>
            {!clientCode
              ? 'Select a client, organisation and branch to load forms.'
              : 'This client has no organisation or branch to load forms for.'}
          </EmptyState>
        )}

        {ready && deletingName && <Loader label={`deleting "${deletingName}"`} />}

        {ready && forms === null && !formsError && !deletingName && <Loader label="fetching forms" />}

        {ready && formsError && (
          <p className="field-hint" style={{ color: 'var(--danger, #e5484d)' }}>
            getForms failed: {formsError}
          </p>
        )}

        {ready && forms !== null && !deletingName && (
          <>
            <table className="term-table">
              <thead>
                <tr>
                  <th>name</th><th>module</th><th>url</th><th>seq</th><th>active</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pagedForms.map((f) => (
                  <tr key={f.formId}>
                    <td>{f.formName}</td>
                    <td>{moduleName(f.ModuleId)}</td>
                    <td>{f.formUrl}</td>
                    <td>{f.formSequence}</td>
                    <td>{f.isActive ? 'yes' : 'no'}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className="term-btn term-btn--xs" disabled={busy} onClick={() => openEdit(f)}>edit</button>
                      <button className="term-btn term-btn--xs term-btn--danger" disabled={busy} onClick={() => handleDelete(f)}>del</button>
                    </td>
                  </tr>
                ))}
                {total === 0 && (
                  <tr><td colSpan={6} className="field-hint">No forms yet.</td></tr>
                )}
              </tbody>
            </table>

            {total > 0 && (
              <Pagination
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
              />
            )}
          </>
        )}
      </Panel>

      {form && (
        <Modal
          title={form.formId ? `// EDIT_FORM #${form.formId}` : '// NEW_FORM'}
          closable={!busy}
          onClose={closeForm}
        >
          <div className="form-grid">
            <label>module
              <SearchSelect
                width="100%"
                value={form.ModuleId}
                disabled={busy}
                placeholder="select module"
                options={(modules || []).map((m) => ({ value: m.moduleId, label: m.moduleName }))}
                onChange={(v) => setForm({ ...form, ModuleId: Number(v) })}
              />
            </label>
            <label>formName
              <input className="term-input" value={form.formName} disabled={busy}
                onChange={(e) => setForm({ ...form, formName: e.target.value })} />
            </label>
            <label>formUrl
              <input className="term-input" value={form.formUrl} disabled={busy}
                onChange={(e) => setForm({ ...form, formUrl: e.target.value })} />
            </label>
            <label>formSequence
              <input className="term-input" type="number" value={form.formSequence} disabled={busy}
                onChange={(e) => setForm({ ...form, formSequence: Number(e.target.value) })} />
            </label>
            <label>description
              <input className="term-input" value={form.description} disabled={busy}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label>fineYear
              <input className="term-input" value={form.fineYear} disabled={busy}
                onChange={(e) => setForm({ ...form, fineYear: e.target.value })} />
            </label>
            <label>videoLink
              <input className="term-input" value={form.videoLink} disabled={busy}
                onChange={(e) => setForm({ ...form, videoLink: e.target.value })} />
            </label>
            <label>pdfPath
              <input className="term-input" value={form.pdfPath} disabled={busy}
                onChange={(e) => setForm({ ...form, pdfPath: e.target.value })} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.isActive} disabled={busy}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              isActive
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="term-btn term-btn--solid" disabled={busy} onClick={submitForm}>
              {busy ? 'saving...' : 'save'}
            </button>
            <button className="term-btn" disabled={busy} onClick={closeForm}>cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}